import React, { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Box, Button, Slider } from "@mui/material";
import getCroppedImg from "../../components/utils/cropImage";
import axiosInstance from "../../axios/axios";
import { toast } from "react-toastify";

interface Props {
  file: File;
  isOrg?:boolean;
  onClose: () => void;
  onComplete: (preview: string) => void;
}

const ImageUploaderWithCrop = ({ file, onClose, onComplete,isOrg }: Props) => {
  const [imageSrc, setImageSrc] = useState<string>("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_: any, croppedArea: any) => {
    setCroppedAreaPixels(croppedArea);
  }, []);

  const handleApply = async () => {
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      if (!croppedBlob) {
        toast.error("Could not crop image");
        return;
      }
  
      const formData = new FormData();
      formData.append("picture", croppedBlob, file.name || "cropped.jpg");
      const url = isOrg
        ? "/authentication/org-pic-upload": "/authentication/user-pic-upload";
      const response = await axiosInstance.post(
        url,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const msg = response?.data?.message || "";
      // Backend returns 200 with message "Admins only change or upload profile picture." for free trial users
      // Treat that as an error on the client side (do not show success or call onComplete)
      if (typeof msg === "string" && msg.toLowerCase().includes("admins only")) {
        toast.error(msg || "Only admins can upload or change the profile picture.");
        return;
      }

      toast.success(msg || "Profile picture uploaded");
      onComplete(URL.createObjectURL(croppedBlob));
    } catch (error: any) {
      const serverMessage = error?.response?.data?.message;
      const message =
        typeof serverMessage === "string" && serverMessage.trim().length > 0
          ? serverMessage
          : error?.message || "Upload failed";
      toast.error(message);
      console.error("Upload error:", error);
    }
  };
  
  

  return (
    <Box sx={{ width: 300, height: 420, display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box sx={{ width: 280, height: 280, position: "relative", background: "#333" }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </Box>
      <Slider
        value={zoom}
        min={1}
        max={3}
        step={0.1}
        onChange={(e, value) => setZoom(value as number)}
        sx={{ width: "80%", mt: 2 }}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
        <Button variant="contained" onClick={handleApply}>Apply</Button>
        <Button variant="outlined" onClick={onClose}>Close</Button>
      </Box>
    </Box>
  );
};

export default ImageUploaderWithCrop;
